import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../config/api';
import OtherToolsSection from '../OtherToolsSection';

const faqs = [
  {
    q: 'What is a DKIM record?',
    a: 'A DKIM record is a DNS TXT record that stores the public key used to verify the digital signature of emails sent from your domain.'
  },
  {
    q: 'What is a DKIM selector?',
    a: 'A selector is a string used to locate the DKIM public key in DNS. It is specified in the DKIM-Signature header of emails.'
  },
  {
    q: 'How do I find my DKIM selector?',
    a: 'Check the DKIM-Signature header of an email sent from your domain. The s= tag is the selector.'
  },
  {
    q: 'Why does my DKIM check fail?',
    a: 'Common reasons include missing or incorrect DNS records, mismatched selector, or key misconfiguration.'
  },
  {
    q: 'Can I have multiple DKIM selectors?',
    a: 'Yes, you can have multiple selectors for key rotation or different sending services.'
  },
  {
    q: 'What is key rotation in DKIM?',
    a: 'Key rotation means periodically changing your DKIM keys to enhance security.'
  },
  {
    q: 'What is the difference between DKIM and SPF?',
    a: 'SPF validates the sending server, while DKIM validates the message content and sender.'
  },
  {
    q: 'How do I test my DKIM record?',
    a: 'Use this DKIM Analyser or command-line tools (dig, nslookup) to check your DKIM record.'
  },
  {
    q: 'What happens if my DKIM record is too long?',
    a: 'DKIM records longer than 255 characters must be split into multiple strings in DNS.'
  },
  {
    q: 'What is a DKIM failure?',
    a: 'A DKIM failure occurs when the signature cannot be verified with the public key in DNS.'
  }
];

interface DKIMCheck {
  label: string;
  status: string;
  details: string | null;
}

interface DKIMResult {
  domain: string;
  timestamp: string;
  dkim: {
    selector: string;
    record: string;
  };
  checks: DKIMCheck[];
}

const DKIMAnalyzer: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [selector, setSelector] = useState('google');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DKIMResult | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const validateDomain = (d: string) => /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(d);
  const validateSelector = (s: string) => /^[a-zA-Z0-9._-]+$/.test(s);

  const analyzeDKIMRecord = (dkim: any): DKIMCheck[] => {
    const checks: DKIMCheck[] = [];
    if (!dkim || !dkim.record) {
      checks.push({
        label: 'DKIM Record',
        status: 'Error (No DKIM record found)',
        details: 'No DKIM record was found for this domain and selector.'
      });
      return checks;
    }
    const record = dkim.record;
    // v=DKIM1 check (anywhere in the record)
    const hasVersion = /v=DKIM1/i.test(record);
    checks.push({
      label: 'Version',
      status: hasVersion ? 'Pass' : 'Warning (No v=DKIM1 tag found)',
      details: hasVersion ? 'DKIM version tag present' : 'Best practice: Add v=DKIM1 to your DKIM record.'
    });
    // k= key type
    const keyTypeMatch = record.match(/k=([a-z0-9-]+)/i);
    const keyType = keyTypeMatch ? keyTypeMatch[1] : null;
    checks.push({
      label: 'Key Type',
      status: keyType ? 'Pass' : 'Warning (No key type found)',
      details: keyType ? `Key type: ${keyType}` : 'Add k=rsa (or appropriate) to your DKIM record.'
    });
    // p= public key
    const hasPublicKey = /p=/.test(record);
    checks.push({
      label: 'Public Key',
      status: hasPublicKey ? 'Pass' : 'Warning (No public key found)',
      details: hasPublicKey ? 'Public key is present' : 'Add the p= (public key) value to your DKIM record.'
    });
    // t= flags
    const hasTestFlag = /t=s/.test(record);
    checks.push({
      label: 'Test Mode',
      status: hasTestFlag ? 'Warning (Test mode enabled)' : 'Pass',
      details: hasTestFlag ? 't=s (test mode) is enabled. Remove for production.' : 'Test mode is not enabled.'
    });
    return checks;
  };

  const handleAnalyze = async () => {
    setError('');
    setResult(null);
    if (!domain) {
      setError('Please enter a domain.');
      return;
    }
    if (!validateDomain(domain)) {
      setError('Please enter a valid domain.');
      return;
    }
    if (!selector || !validateSelector(selector)) {
      setError('Please enter a valid selector (letters, numbers, dash, dot, underscore).');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/api/dns/check/DKIM?domain=${encodeURIComponent(domain)}&selector=${encodeURIComponent(selector)}`);
      console.log('DKIM API Response:', response.data); // Debug log
      
      if (!response.data || !response.data.dkim) {
        // Check for specific DKIM error message
        if (response.data && response.data.dkim_error) {
          setError(response.data.dkim_error);
        } else {
        setError('No DKIM record found for this domain and selector.');
        }
        setResult(null);
      } else {
        const resultWithChecks = {
          domain: domain,
          timestamp: new Date().toISOString(),
          dkim: {
            selector: selector,
            record: response.data.dkim.record
          },
          checks: analyzeDKIMRecord({ record: response.data.dkim.record })
        };
        setResult(resultWithChecks);
      }
    } catch (err: any) {
      console.error('DKIM analysis error:', err);
      setError(err.response?.data?.error || 'Failed to fetch DKIM data. Please try again.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!result || !result.dkim || !result.checks) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = pageHeight / 2 - 60;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.setTextColor('#0B60B1');
    doc.text('DKIM AUDIT REPORT', 60, y);
    y += 40;
    doc.setFontSize(18);
    doc.setTextColor('#222');
    doc.text(`Domain: ${domain}`, 60, y);
    y += 28;
    doc.setFontSize(18);
    doc.text(`Selector: ${selector}`, 60, y);
    y += 28;
    doc.setFontSize(12);
    doc.setTextColor('#888');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}   Year: ${now.getFullYear()}`, 60, y);
    y += 22;
    doc.setFontSize(10);
    doc.setTextColor('#888');
    doc.text('Generated by: QuillBee – Deliverability Agent for ImpactQuill', 60, y);
    doc.addPage();
    // Header for page 2
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor('#0B60B1');
    doc.text(`DKIM report for ${domain}`, 40, 110);
    let y2 = 140;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor('#0B60B1');
    doc.text('DKIM Record:', 40, y2);
    y2 += 18;
    doc.setFont('courier', 'normal');
    doc.setFontSize(11);
    doc.setTextColor('#222');
    const dkimLines = doc.splitTextToSize(result.dkim.record, pageWidth - 80);
    dkimLines.forEach((line: string) => {
      doc.text(line, 40, y2);
      y2 += 16;
    });
    y2 += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(result.dkim ? '#388e3c' : '#d32f2f');
    doc.text(`Status: ${result.dkim ? 'PASS' : 'FAIL'}`, 40, y2);
    y2 += 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor('#0B60B1');
    doc.text('DKIM Checks:', 40, y2);
    y2 += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor('#222');
    result.checks.forEach((check: DKIMCheck) => {
      doc.text(`• ${check.label}: ${check.status}`, 60, y2);
      y2 += 18;
      if (check.details) {
        doc.text(`  ${check.details}`, 60, y2);
        y2 += 18;
      }
    });
    doc.save(`dkim-analysis-${domain}.pdf`);
  };

  const handleDownloadJPG = async () => {
    if (!resultRef.current) return;
    const canvas = await html2canvas(resultRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${domain}_dkim_result.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    link.click();
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', py: 0 }}>
      {/* Hero Banner */}
      <Box sx={{ bgcolor: '#0B60B1', color: '#fff', py: 6, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>DKIM Analyzer</Typography>
        <Typography variant="h6" maxWidth={600} mx="auto">
          Analyze and validate your DKIM records for best practices.
        </Typography>
      </Box>
      {/* Input Section */}
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="subtitle1" mb={2}>Enter your domain and selector to check DKIM:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', maxWidth: 500 }}>
          <TextField
            label="Domain"
            variant="outlined"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            fullWidth
            disabled={loading}
            error={!!error}
            helperText={error}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain && selector) handleAnalyze();
            }}
          />
          <TextField
            label="Selector"
            variant="outlined"
            value={selector}
            onChange={e => setSelector(e.target.value)}
            fullWidth
            disabled={loading}
            error={!!error}
            helperText={error ? '' : 'Usually "default" or as set in your mail system.'}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain && selector) handleAnalyze();
            }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAnalyze} 
            disabled={loading || !domain || !selector} 
            sx={{ 
              minWidth: 120,
              height: 56, // Match TextField height
              '& .MuiCircularProgress-root': {
                color: 'white'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Analyze'}
          </Button>
        </Box>
        {loading && <CircularProgress sx={{ mt: 3 }} />}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Box>
      {/* Result Section */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {result && result.dkim && (
        <Card ref={resultRef} sx={{ width: '100%', maxWidth: 900, mx: 'auto', mb: 4, p: 2, boxShadow: 6, borderRadius: 3, bgcolor: '#fff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <MuiLink href={`https://${domain}`} target="_blank" rel="noopener noreferrer" underline="hover" variant="subtitle1" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {domain}
                </MuiLink>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                  Selector: <b>{selector}</b>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy DKIM Record'}>
                  <IconButton onClick={() => { navigator.clipboard.writeText(result.dkim.record); setCopied(true); setTimeout(() => setCopied(false), 1200); }}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton onClick={handleDownloadPDF}>
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as JPG">
                  <IconButton onClick={handleDownloadJPG}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={result.dkim && result.dkim.record ? 'PASS' : 'FAIL'}
                color={result.dkim && result.dkim.record ? 'success' : 'error'}
                sx={{ 
                  fontWeight: 700, 
                  fontSize: 16, 
                  px: 2, 
                  py: 1, 
                  mb: 1,
                  '& .MuiChip-icon': {
                    color: 'inherit'
                  }
                }}
              />
            </Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>DKIM Record</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f7fafd', mb: 2 }}>
              <Typography variant="body1" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 16 }}>
                {result.dkim.record}
              </Typography>
            </Paper>
            <Divider sx={{ my: 2 }} />
            {/* DKIM Analysis Table */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>DKIM Analysis</Typography>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, bgcolor: '#f7fafd', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                <tbody>
                  {Array.isArray(result.checks) && result.checks.length > 0 && result.checks.map((check: DKIMCheck, i: number) => (
                    <tr key={i}>
                      <td style={{ verticalAlign: 'top', padding: '12px 8px 12px 16px', width: 40, borderBottom: '1px solid #e0e0e0' }}>
                        {check.status.includes('Pass') ? (
                          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22, mt: 0.5 }} />
                        ) : check.status.includes('Warning') ? (
                          <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 22, mt: 0.5 }} />
                        ) : (
                          <ErrorIcon sx={{ color: '#d32f2f', fontSize: 22, mt: 0.5 }} />
                        )}
                      </td>
                      <td style={{ verticalAlign: 'top', padding: '12px 8px', fontWeight: 700, width: '180px', borderBottom: '1px solid #e0e0e0' }}>
                        {check.label}
                      </td>
                      <td style={{ verticalAlign: 'top', padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                        {check.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            {/* DKIM Card Grid Breakdown */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Tag Breakdown</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {Array.isArray(result.checks) && result.checks.length > 0 && result.checks.map((check: DKIMCheck, i: number) => (
                  <Paper key={i} elevation={2} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
                    {check.status.includes('Pass') ? (
                      <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28, mt: 0.5 }} />
                    ) : check.status.includes('Warning') ? (
                      <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: 28, mt: 0.5 }} />
                    ) : (
                      <ErrorIcon sx={{ color: '#d32f2f', fontSize: 28, mt: 0.5 }} />
                    )}
                    <Box>
                      <Typography fontWeight={700}>{check.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{check.details}</Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>DKIM FAQ</Typography>
          {faqs.map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>
      <OtherToolsSection current="DKIM Analyzer" />
    </Box>
  );
};

export default DKIMAnalyzer; 